import React, { useState, useRef, useEffect } from "react";
import makeCloudinaryUrlForPhoto from '../../react/makeCloudinaryUrlForPhoto'
import './MenuCategoriesPreview.scss'

// CategoryGrid (ES6 React, no Tailwind)
// Now: supports image preloading with fallback placeholder when the next image hasn't finished loading.

const cn = (...xs) => xs.filter(Boolean).join(" ");

function CategoryTile({ cat }) {
  const products = Array.isArray(cat?.products) ? cat.products : [];
  const [idx, setIdx] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);
  const touchStartX = useRef(null);

  const count = products.length;
  const title = cat?.name?.en || "Untitled";

  const preloadImage = (photo) => {
    return new Promise((resolve, reject) => {
      if (!photo) return reject();
      const url = makeCloudinaryUrlForPhoto(photo, {width: 600});
      const img = new Image();
      img.src = url;
      img.onload = () => resolve(url);
      img.onerror = reject;
    });
  };

  useEffect(() => {
    const current = products[idx];
    if (current?.photo) {
      setImgLoaded(false);
      preloadImage(current.photo)
        .then((url) => {
          setImgUrl(url);
          setImgLoaded(true);
        })
        .catch(() => {
          setImgUrl(null);
          setImgLoaded(true);
        });
    } else {
      setImgUrl(null);
      setImgLoaded(true);
    }
  }, [idx, products]);

  const go = (dir) => {
    if (!count) return;
    setIdx((i) => (i + dir + count) % count);
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    const threshold = 40;
    if (Math.abs(delta) > threshold) {
      if (delta < 0) go(+1);
      else go(-1);
    }
    touchStartX.current = null;
  };

  const currentProduct = products[idx];
  const productName = currentProduct?.name?.en || currentProduct?.name || "";

  return (
    <div className="category-tile" role="group" aria-label={title}>
      <div
        className="category-tile__media"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {!imgLoaded ? (
          <div className="category-tile__fallback">
            <span>Loading...</span>
          </div>
        ) : imgUrl ? (
          <img
            src={imgUrl}
            alt={productName || title}
            loading="lazy"
            className="category-tile__img"
          />
        ) : (
          <div className="category-tile__fallback">
            <span>No image</span>
          </div>
        )}

        <div className="category-tile__overlay" />

        <div className="category-tile__text">
          <h3 className="category-tile__title">{title}</h3>
          {productName ? <p className="category-tile__product">{productName}</p> : null}
          {cat?.description?.en ? <p className="category-tile__desc">{cat.description.en}</p> : null}
        </div>

        {count > 1 && (
          <>
            <button
              type="button"
              className="category-tile__nav category-tile__nav--prev"
              aria-label="Previous product"
              onClick={() => go(-1)}
            >
              <span aria-hidden>‹</span>
            </button>
            <button
              type="button"
              className="category-tile__nav category-tile__nav--next"
              aria-label="Next product"
              onClick={() => go(+1)}
            >
              <span aria-hidden>›</span>
            </button>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="category-tile__dots" role="tablist" aria-label="Products pagination">
          {products.map((_, i) => (
            <button
              key={i}
              type="button"
              className={cn("category-tile__dot", i === idx && "is-active")}
              onClick={() => setIdx(i)}
              role="tab"
              aria-selected={i === idx}
              aria-label={`Go to product ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryGrid({
  categories = [],
  className,
}) {
  return (
    <div className={cn("category-grid", className)}>
      {categories
        .filter((cat) => {
          if (cat?.tag && String(cat.tag).indexOf('hid') !== -1) return false;
          return true;
        })
        .filter((cat) => {
            const products = Array.isArray(cat?.products) ? cat.products : [];
            return products.some(product => product?.photo);
        })
        .map((cat) => (
          <CategoryTile key={String(cat.id)} cat={cat} />
        ))}
    </div>
  );
}

export default CategoryGrid;